import { useState, useEffect } from "react";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import Select from "react-select";
import HospitalSelect from "../../selectFilters/HospitalSelect";
import CustomPagination from "../../utils/paginationUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "./RequirementReport.scss";
import { handleApiErrorResponses } from "../../utils/ApisErrorHandling";
import { HospitalServices } from "../../services/HospitalServices";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";

const RequirementReport = () => {
  const [loading, setLoading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [pendingDocs, setPendingDocs] = useState(null);
  const [hospitalId, setHospitalId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextStageActions, setNextStageActions] = useState(null);
  // ✅ CORRECTED: Proper lifecycle order
  const [selectedStages, setSelectedStages] = useState([
    {
      value: "Request to Admission",
      label: "Reference Document Pending",
    },
    {
      value: "Admission Documents Upload Pending",
      label: "Admission Documents Upload Pending",
    },
    {
      value: "Admission Documents Rejected by team B",
      label: "Admission Documents Rejected",
    },
    {
      value: "Admission Approved on TMS",
      label: "Admission Approved on TMS",
    },
    {
      value: "Enhancement Documents Upload Pending",
      label: "Enhancement Documents Upload Pending",
    },
    {
      value: "Enhancement Documents Rejected by Team D",
      label: "Enhancement Documents Rejected",
    },
    {
      value: "Enhancement Approved on TMS",
      label: "Enhancement Approved on TMS",
    },
    {
      value: "Claim Documents Upload Pending",
      label: "Claim Documents Upload Pending",
    },
    {
      value: "Claim Documents Rejected by Team D",
      label: "Claim Documents Rejected",
    },
  ]);

  const limit = 10;

  // ✅ CORRECTED: Same order in dropdown options
  const stagesName = [
    {
      value: "Request to Admission",
      label: "Reference Document Pending",
    },
    {
      value: "Admission Documents Upload Pending",
      label: "Admission Documents Upload Pending",
    },
    {
      value: "Admission Documents Rejected by team B",
      label: "Admission Documents Rejected",
    },
    {
      value: "Admission Approved on TMS",
      label: "Admission Approved on TMS",
    },
    {
      value: "Enhancement Documents Upload Pending",
      label: "Enhancement Documents Upload Pending",
    },
    {
      value: "Enhancement Documents Rejected by Team D",
      label: "Enhancement Documents Rejected",
    },
    {
      value: "Enhancement Approved on TMS",
      label: "Enhancement Approved on TMS",
    },
    {
      value: "Claim Documents Upload Pending",
      label: "Claim Documents Upload Pending",
    },
    {
      value: "Claim Documents Rejected by Team D",
      label: "Claim Documents Rejected",
    },
  ];

  // ✅ ADDED: Stage order mapping for sorting
  const stageOrder = {
    "Request to Admission": 1,
    "Admission Documents Upload Pending": 2,
    "Admission Documents Rejected by team B": 3,
    "Admission Approved on TMS": 4,
    "Enhancement Documents Upload Pending": 5,
    "Enhancement Documents Rejected by Team D": 6,
    "Enhancement Approved on TMS": 7,
    "Claim Documents Upload Pending": 8,
    "Claim Documents Rejected by Team D": 9,
  };

  // ✅ ADDED: Function to sort cases by lifecycle order
  const sortCasesByStage = (casesObject) => {
    const sortedCases = {};

    Object.keys(casesObject)
      .sort((a, b) => (stageOrder[a] || 999) - (stageOrder[b] || 999))
      .forEach((stage) => {
        sortedCases[stage] = casesObject[stage];
      });

    return sortedCases;
  };

  const formatPendingFromWithDuration = (pendingDate, generatedAt) => {
    if (!pendingDate) return "-";

    const pending = new Date(pendingDate).getTime();
    const generated = new Date(generatedAt).getTime();

    const diffMs = generated - pending;

    if (diffMs < 0) {
      return `${new Date(pendingDate).toLocaleDateString("en-GB")}\n0d 0h 0m`;
    }

    const totalMinutes = Math.floor(diffMs / (1000 * 60));

    const days = Math.floor(totalMinutes / (60 * 24));

    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);

    const minutes = totalMinutes % 60;

    const formattedDate = new Date(pendingDate).toLocaleDateString("en-GB");

    return `${formattedDate}\n${days}d ${hours}h ${minutes}m`;
  };

  const getHospitalBasedPdf = (stagesData, hospitalName, generatedAt) => {
    const doc = new jsPDF("l", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Settlemed Daily Requirement Report", pageWidth / 2, 20, {
      align: "center",
    });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`${hospitalName || "-"}`, pageWidth / 2, 30, {
      align: "center",
    });

    // Note
    doc.setFontSize(11);
    doc.setFont("helvetica", "italic");
    const noteText =
      "Note- Please note that the following patient documents are pending upload across various " +
      "stages (Admission, Enhancement, Discharge) on HSA portal. Kindly ensure that all " +
      "documents are uploaded promptly to avoid delays in claim processing.";

    const splitNote = doc.splitTextToSize(noteText, pageWidth - 40);
    doc.text(splitNote, 20, 40);
    let startY = 40 + splitNote.length * 5 + 10;
    // ✅ SORT STAGES BEFORE LOOPING
    const sortedCases = sortCasesByStage(stagesData);

    // Loop through sorted stages
    Object.keys(sortedCases).forEach((stageName) => {
      const cases = sortedCases[stageName];
      if (!cases || cases.length === 0) return;

      const displayStageName = stageName.includes("Request to Admission")
        ? "Reference Document Upload Pending"
        : stageName;
      const displayNextTask = nextStageActions
        ? nextStageActions[`${stageName}`]
        : "";
      const splitNextStage = doc.splitTextToSize(
        displayNextTask,
        pageWidth - 40,
      );
      console.log("display next task:", displayNextTask);
      // Prepare table data with Registered Date column added
      const tableData = cases.map((c, idx) => [
        String(idx + 1),
        c.patient?.patientName || "-",
        String(c.patient?.age || "-"),
        String(c.pmjayRegistrationId || "-"),
        c.caseType
          ? c.caseSubType === "DIALYSIS"
            ? `${c.caseType} (${c.caseSubType})`
            : c.caseType
          : "-",
        formatPendingFromWithDuration(
          c.currentSubMasterStage?.createdAt,
          generatedAt,
        ),
        c.patient?.gender || "-",
        c.admissionDate
          ? new Date(c.admissionDate).toLocaleDateString("en-GB")
          : "-",
        c.patient?.registeredDate
          ? new Date(c.patient.registeredDate).toLocaleDateString("en-GB")
          : "-",
      ]);

      // Add stage title before table
      if (startY > 20) {
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(displayStageName, 20, startY);
        // doc.text(displayNextTask, 20, startY);
        startY += 8;
      }
      if (
        displayNextTask &&
        typeof displayNextTask === "string" &&
        displayNextTask.trim() !== ""
      ) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text(splitNextStage, 20, startY);
        startY += 8;
      }

      // Generate table with Registered Date column
      autoTable(doc, {
        startY: startY,
        head: [
          [
            "No.",
            "Patient Name",
            "Age",
            "ID",
            "Case Type",
            "Pending From",
            "Gender",
            "Admission Date",
            "Registered Date",
          ],
        ],
        body: tableData,
        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 2,
          valign: "middle",
          overflow: "linebreak",
        },

        headStyles: {
          fillColor: [220, 220, 220],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 9,
        },

        columnStyles: {
          0: { cellWidth: 10 },
        },

        margin: { left: 14, right: 14 },
        tableWidth: "auto",
      });

      startY = doc.lastAutoTable.finalY + 10;
    });

    const fileName = `${(hospitalName || "Unknown-Hospital").replace(
      /\s+/g,
      "-",
    )}-Daily-Requirement-Report.pdf`;
    doc.save(fileName);
  };

  const downloadPdf = async () => {
    if (!pendingDocs || pendingDocs.length === 0) {
      console.log("No pending documents to download");
      return;
    }

    setLoading(true);

    const generatedAt = new Date();

    try {
      for (let data of pendingDocs) {
        await getHospitalBasedPdf(data?.cases, data?.name, generatedAt);

        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    const generatedAt = new Date();
    if (!pendingDocs || pendingDocs.length === 0) {
      toast.info("No pending documents to download.");
      return;
    }

    setLoading(true);

    try {
      const workbook = XLSX.utils.book_new();
      const excelData = [];

      pendingDocs.forEach((hospitalData) => {
        const hospitalName = hospitalData?.name || "Unknown Hospital";
        const sortedCases = sortCasesByStage(hospitalData?.cases || {});

        Object.keys(sortedCases).forEach((stageName) => {
          const cases = sortedCases[stageName];

          if (!cases || cases.length === 0) {
            return;
          }

          const displayStageName = stageName.includes("Request to Admission")
            ? "Reference Document Upload Pending"
            : stageName;

          const nextAction = nextStageActions?.[stageName] || "";

          // Stage heading
          excelData.push({
            Hospital: hospitalName,
            Stage: displayStageName,
            "Next Action": nextAction,
            "No.": "",
            "Patient Name": "",
            Age: "",
            ID: "",
            "Case Type": "",
            "Pending From": "",
            Gender: "",
            "Admission Date": "",
            "Registered Date": "",
          });

          cases.forEach((c, idx) => {
            excelData.push({
              Hospital: hospitalName,
              Stage: displayStageName,
              "Next Action": nextAction,
              "No.": idx + 1,
              "Patient Name": c?.patient?.patientName || "-",
              Age: c?.patient?.age || "-",
              ID: c?.pmjayRegistrationId || "-",

              "Case Type": c?.caseType
                ? c?.caseSubType === "DIALYSIS"
                  ? `${c.caseType} (${c.caseSubType})`
                  : c.caseType
                : "-",

              "Pending From": formatPendingFromWithDuration(
                c?.currentSubMasterStage?.createdAt,
                generatedAt,
              ),

              Gender: c?.patient?.gender || "-",

              "Admission Date": c?.admissionDate
                ? new Date(c.admissionDate).toLocaleDateString("en-GB")
                : "-",

              "Registered Date": c?.patient?.registeredDate
                ? new Date(c.patient.registeredDate).toLocaleDateString("en-GB")
                : "-",
            });
          });

          // Blank row between stages
          excelData.push({
            Hospital: "",
            Stage: "",
            "Next Action": "",
            "No.": "",
            "Patient Name": "",
            Age: "",
            ID: "",
            "Case Type": "",
            "Pending From": "",
            Gender: "",
            "Admission Date": "",
            "Registered Date": "",
          });
        });
      });

      if (excelData.length === 0) {
        toast.info("No data present to generate excel.");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 25 },
        { wch: 40 },
        { wch: 45 },
        { wch: 8 },
        { wch: 25 },
        { wch: 10 },
        { wch: 22 },
        { wch: 22 },
        { wch: 25 },
        { wch: 12 },
        { wch: 18 },
        { wch: 20 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "Requirement Report");

      XLSX.writeFile(workbook, "Settlemed-Daily-Requirement-Report.xlsx");
    } catch (error) {
      console.error("Excel download error:", error);
      handleApiErrorResponses(error);
    } finally {
      setLoading(false);
    }
  };

  const getNextStateAction = async () => {
    try {
      const res = await HospitalServices?.getSubStageNextAction();
      setNextStageActions(res?.data?.data);
      console.log("next atge:", res?.data);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const getPendingDocs = async () => {
    if (!selectedStages || selectedStages.length === 0) {
      setPendingDocs(null);
      setTotalPages(1);
      return;
    }

    setLoading(true);
    try {
      const modifiedIds = hospitalId?.map((id) => id?.value);
      const modifiedStages = selectedStages.map((item) => item.value);
      const res = await HospitalServices.getHospitalPendingDocList(
        page,
        limit,
        modifiedIds?.length > 0 ? modifiedIds : null,
        modifiedStages?.length > 0 ? modifiedStages : null,
      );

      setPendingDocs(res?.data?.docList);
      setTotalPages(res?.data?.totalPages);
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getNextStateAction();
  }, []);
  useEffect(() => {
    getPendingDocs();
  }, [selectedStages, hospitalId, page]);
  console.log("next stage:", nextStageActions);

  return (
    <div className="requirement-box">
      <span className="path_line">
        <WidgetsOutlinedIcon style={{ height: "24px" }} /> &nbsp; &nbsp;/ &nbsp;
        &nbsp; Dashboard &nbsp; &nbsp;/ &nbsp; &nbsp; Requirement Report
      </span>

      <div className="flex justify-between items-center gap-3">
        <div className="heading flex-shrink-0">Requirement Report</div>
        <div className="flex gap-2 items-start">
          <div className="min-w-[300px]">
            <HospitalSelect
              onChange={setHospitalId}
              hospitalIds={hospitalId}
              isMulti={true}
            />
          </div>
          <div className="min-w-[300px]">
            <Select
              isMulti
              options={stagesName}
              getOptionLabel={(options) => options.label}
              getOptionValue={(options) => options.value}
              onChange={(items) => {
                setSelectedStages(items);
              }}
              value={selectedStages}
              placeholder="SELECT STAGE"
            />
          </div>
        </div>
      </div>
      <hr
        style={{ width: "100%", border: "1px solid rgba(228, 228, 228, 1)" }}
      />
      <div className="flex items-center gap-2 justify-end">
        <div className="relative shrink-0 mt-4">
          {/* Download Button */}
          <button
            type="button"
            disabled={
              loading ||
              !pendingDocs ||
              !selectedStages ||
              selectedStages.length === 0
            }
            onClick={() => {
              if (!loading) {
                setShowDownloadMenu((prev) => !prev);
              }
            }}
            className="flex items-center justify-center gap-2 border border-none border-[#017C71] rounded-full px-5 py-[13px] bg-[#017C71] text-white text-sm font-medium cursor-pointer min-w-[180px] disabled:cursor-not-allowed"
          >
            {loading ? (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{
                  color: "#FFFFFF",
                }}
              />
            ) : (
              <>
                <span>Download Report</span>

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${
                    showDownloadMenu ? "rotate-180" : ""
                  }`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </>
            )}
          </button>

          {/* Download Dropdown */}
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-0 w-full min-w-[180px] bg-white border border-[#AFAFAF] rounded-b-[10px] rounded-t-none overflow-hidden z-50 shadow-sm">
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (pendingDocs?.length > 0) {
                    downloadPdf();
                  } else {
                    toast.info("No data present to generate pdf.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download PDF
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setShowDownloadMenu(false);

                  if (pendingDocs?.length > 0) {
                    downloadExcel();
                  } else {
                    toast.info("No data present to generate excel.");
                  }
                }}
                className="w-full text-left px-4 py-3 text-sm text-[#191919] bg-white hover:bg-[#F8F8F8] border-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ✅ UPDATED: Display section with sorted data */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center">
            <p>Loading...</p>
          </div>
        ) : !selectedStages || selectedStages.length === 0 ? (
          // ✅ Show message when no filters selected
          <div className="text-center p-8 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">No Data Found</p>
          </div>
        ) : pendingDocs && pendingDocs.length > 0 ? (
          pendingDocs?.map((doc, idx) => {
            const sortedCases = sortCasesByStage(doc?.cases);

            return (
              <div key={idx}>
                <div className="font-bold text-lg">{doc?.name}</div>
                {Object.keys(sortedCases)?.map((stage, sIdx) => (
                  <div key={sIdx} className="mb-6">
                    <h2 className="font-bold text-lg">
                      {stage.includes("Request to Admission")
                        ? "Reference Document Upload Pending"
                        : stage}
                    </h2>
                    <span>
                      {nextStageActions && nextStageActions[`${stage}`]}
                    </span>
                    <table className="w-full border-collapse border border-gray-400 mt-2">
                      <thead>
                        <tr>
                          <th className="border border-gray-400 p-2 text-left">
                            No.
                          </th>
                          <th className="border border-gray-400 p-2 text-left">
                            Patient
                          </th>
                          <th className="border border-gray-400 p-2 text-left">
                            Age
                          </th>
                          <th className="border border-gray-400 p-2 text-left">
                            ID
                          </th>
                          <th className="border border-gray-400 p-2 text-left">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCases[stage]?.map((c, cIdx) => (
                          <tr key={cIdx}>
                            <td className="border border-gray-400 p-2">
                              {cIdx + 1}
                            </td>
                            <td className="border border-gray-400 p-2">
                              {c?.patient?.patientName}
                            </td>
                            <td className="border border-gray-400 p-2">
                              {c?.patient?.age}
                            </td>
                            <td className="border border-gray-400 p-2">
                              {c?.pmjayRegistrationId}
                            </td>
                            <td className="border border-gray-400 p-2">
                              {c?.patient?.registeredDate
                                ? new Date(
                                    c?.patient?.registeredDate,
                                  ).toLocaleString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  })
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <div className="text-center p-4">
            <p>No pending documents found</p>
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <CustomPagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default RequirementReport;
